"use client";

import Link from "next/link";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";
import { SITE_CONTROL_NAME } from "@/lib/site-control-copy";

/**
 * Slim "audience-switch" band shown directly below the homepage hero — catches
 * principal contractors running multiple live sites early, without touching the
 * hero's primary free-tier CTA (the funnel that drives most signups).
 *
 * Self-gating on the `site_control` flag (read from the marketing layout's
 * AppFeatureFlagsProvider): renders nothing when the flag is off, so it appears
 * and disappears in lockstep with the rest of the Site Control surface. Kept a
 * client child so the homepage hero keeps streaming without blocking on a DB
 * flag read.
 */
export function SiteControlHeroBand() {
  const enabled = useAppFeatureFlag("site_control");
  if (!enabled) return null;

  return (
    <aside
      data-testid="site-control-hero-band"
      aria-label={`${SITE_CONTROL_NAME} for principal contractors`}
      className="border-y border-navy/10 bg-navy/5 px-4 py-3 sm:px-6"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-x-4 gap-y-1.5 text-center sm:flex-row sm:justify-between sm:text-left">
        <p className="text-sm text-muted">
          <strong className="font-semibold text-navy">
            Running multiple live sites with subcontractors?
          </strong>{" "}
          {SITE_CONTROL_NAME} is our per-site enterprise tier — entrance QR
          sign-in, CSCS checks, live attendance and audit-grade exports.
        </p>
        <Link
          href="/site-control"
          className="shrink-0 whitespace-nowrap text-sm font-semibold text-brand-700 hover:text-accent-dark"
        >
          Explore {SITE_CONTROL_NAME} &rarr;
        </Link>
      </div>
    </aside>
  );
}
